import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth }              from "./context/AuthContext";
import { SettingsProvider }                   from "./context/SettingsContext";
import useTenantPwaIcons                       from "./hooks/useTenantPwaIcons";
import { NotificationProvider }               from "./context/NotificationContext";
import LandingPageSwitcher from "./pages/LandingPageSwitcher";
import PropertySwitchPage from "./pages/PropertySwitchPage";
import GuestOrderPage from "./pages/GuestOrderPage";
import LoginPage      from "./pages/LoginPage";
import AdminLayout    from "./components/admin/AdminLayout";
import Dashboard      from "./pages/Dashboard";
import CalendarPage   from "./pages/CalendarPage";
import BookingsPage   from "./pages/BookingsPage";
import CustomersPage  from "./pages/CustomersPage";
import FinancialsPage from "./pages/FinancialsPage";
import ReportsPage    from "./pages/ReportsPage";
import RoomsPage      from "./pages/RoomsPage";
import RatesPage from "./pages/RatesPage";
import PublicBooking  from "./pages/PublicBooking";
import GuestFormPage  from "./pages/GuestFormPage";
import RoomDetailPage from "./pages/RoomDetailPage";
import ReviewFormPage from "./pages/ReviewFormPage";
import ReviewsPage    from "./pages/ReviewsPage";
import PromotionsPage from "./pages/PromotionsPage";
import LogsPage       from "./pages/LogsPage";
import SettingsPage   from "./pages/SettingsPage";
import ProfilePage   from "./pages/ProfilePage";
import TeamPage   from "./pages/TeamPage";
import ExpensesPage   from "./pages/ExpensesPage";
import HousekeepingPage from "./pages/HousekeepingPage";
import POSPage         from "./pages/POSPage";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import PullToRefresh from "./components/PullToRefresh";

function ProtectedRoute({ children }) {
  const { token, checked } = useAuth();
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return token ? children : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { token, login } = useAuth();
  // Swaps the installed-app icon/title from HostSync's placeholder to this
  // tenant's own — iOS needs it done in the DOM, see the hook.
  useTenantPwaIcons();
  return (
    <Routes>
      {/* Landing page — root */}
      <Route path="/" element={<LandingPageSwitcher />} />
      {/* Public guest booking */}
      <Route path="/book" element={<PublicBooking />} />
      {/* Public post-check-in guest registration (shared link) */}
      <Route path="/guest-form/:token" element={<GuestFormPage />} />
      {/* Public room detail page (gallery, reviews, live calendar) */}
      <Route path="/rooms/:id" element={<RoomDetailPage />} />
      {/* Multi-property switcher landing — public on purpose: it runs on the
          property being switched TO, where there's no session yet. The
          single-use code in the URL is what authenticates. */}
      <Route path="/switch" element={<PropertySwitchPage />} />
      {/* Public in-room menu ordering — the token identifies the guest's own
          booking, so an order can only ever be charged to that stay. */}
      <Route path="/order/:token" element={<GuestOrderPage />} />
      {/* Public post-checkout review form (shared link) */}
      <Route path="/review/:token" element={<ReviewFormPage />} />
      {/* Login */}
      <Route path="/login"
        element={token ? <Navigate to="/admin/dashboard" replace /> : <LoginPage onLogin={login} />} />
      {/* Admin — protected */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <NotificationProvider>
            <AdminLayout />
          </NotificationProvider>
        </ProtectedRoute>
      }>
        <Route index              element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard"   element={<Dashboard />} />
        <Route path="calendar"    element={<CalendarPage />} />
        <Route path="bookings"    element={<BookingsPage />} />
        <Route path="customers"   element={<CustomersPage />} />
        <Route path="rooms"       element={<RoomsPage />} />
        <Route path="rates"       element={<RatesPage />} />
        <Route path="reviews"     element={<ReviewsPage />} />
        <Route path="promotions"  element={<PromotionsPage />} />
        <Route path="financials"  element={<FinancialsPage />} />
        <Route path="reports"     element={<ReportsPage />} />
        <Route path="logs"        element={<LogsPage />} />
        <Route path="settings"    element={<SettingsPage />} />
        <Route path="profile"     element={<ProfilePage />} />
        <Route path="team"        element={<TeamPage />} />
        <Route path="expenses"    element={<ExpensesPage />} />
        <Route path="housekeeping" element={<HousekeepingPage />} />
        <Route path="pos"         element={<POSPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// InstallGate (checked /api/install/status, rendered <InstallWizard/> on a
// fresh deployment) is REMOVED — that route was unmounted from the backend
// once tenant provisioning replaced the single-deployment install wizard
// (see backend/routes/install.js's deprecation note, MIGRATION-NOTES.md
// §9). There is no more "is this deployment installed" concept once one
// process serves many tenants: a tenant that resolves via Host is already
// provisioned by definition; an unprovisioned/suspended one 404s from
// every API call via resolveTenant itself (CLAUDE.md I2), which is the
// correct behavior — the frontend doesn't need a separate gate for it.
// Leaving InstallGate in used to make every real tenant see the install
// wizard instead of their own app, since /api/install/status no longer
// exists and the fetch's resulting 404 body has no `installed` field
// (found by actually running this in a browser, not by inspection).
export default function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <PwaInstallPrompt />
          <PullToRefresh />
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}
